import {
    api_get_email_notification_list_url,
    api_create_email_notification_url,
    api_update_email_notification_url,
    api_activate_deactivate_email_notification_url,
    api_get_email_notification_details_url
} from "./emailNotificationApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    parser_get_email_notification_list,
    parser_create_email_notification,
    parser_update_email_notification,
    parser_activate_deactivate_email_notification,
    parser_get_email_notification_details
} from "./emailNotificationParser";
import { PAGE } from "../../helpers/constants";

export const get_email_notification_list = createAsyncThunk(
    "email_notification",
    async (payload) => {
        try {
            const { page, limit, qn, qs } = payload;
            const response = await api_get_email_notification_list_url(page, limit, qn, qs);
            const data = parser_get_email_notification_list(response);
            return data;
        } catch (error) {
            throw new Error(error);
        }
    }
);


export const create_email_notification = createAsyncThunk(
    "create_email_notification",
    async (payload) => {
        try {
            // Assuming payload keys match what we want to send, specific to email notification
            const { stage_id, company_id, process_id, sent_to, cc, subject, body } = payload
            const formData = new FormData();
            formData.append("stage_id", stage_id);
            formData.append("company_id", company_id);
            formData.append("process_id", process_id);
            if (sent_to) formData.append("sent_to", sent_to);
            if (cc) formData.append("cc", cc);
            if (subject) formData.append("subject", subject);
            if (body) formData.append("body", body);

            const response = await api_create_email_notification_url(formData);
            const data = parser_create_email_notification(response);
            return { data };
        } catch (error) {
            throw new Error(error);
        }
    }
);

export const update_email_notification = createAsyncThunk(
    "update_email_notification",
    async (payload) => {
        try {
            const { email_notification_id, updated_data } = payload
            const formData = new FormData();
            if (updated_data.stage_id) formData.append("stage_id", updated_data.stage_id);
            if (updated_data.company_id) formData.append("company_id", updated_data.company_id);
            if (updated_data.process_id) formData.append("process_id", updated_data.process_id);
            if (updated_data.sent_to) formData.append("sent_to", updated_data.sent_to);
            if (updated_data.cc) formData.append("cc", updated_data.cc);
            if (updated_data.subject) formData.append("subject", updated_data.subject);
            if (updated_data.body) formData.append("body", updated_data.body);

            const response = await api_update_email_notification_url(email_notification_id, formData);
            const data = parser_update_email_notification(response);
            return { data };
        } catch (error) {
            throw new Error(error);
        }
    }
);

export const activate_deactivate_email_notification = createAsyncThunk(
    "activate_email_notification_email_notification",
    async (payload) => {
        try {
            const { email_notification_id, updated_data } = payload
            const response = await api_activate_deactivate_email_notification_url(email_notification_id, updated_data)
            const data = parser_activate_deactivate_email_notification(response);
            return { data };
        } catch (error) {
            throw new Error(error);
        }
    }
);

export const get_email_notification_details = createAsyncThunk(
    "get_email_notification_details",
    async (payload) => {
        try {
            const response = await api_get_email_notification_details_url(payload);
            const data = parser_get_email_notification_details(response);
            return { data };
        } catch (error) {
            throw new Error(error);
        }
    }
);

const initialState = {
    common_loading: false,
    is_email_notification_modal_open: false,
    selected_status: '',
    search_query: '',
    selected_data: null,
    c_action: null,
    email_notification_id: null,
    email_notification_list: [],
    total_email_notification_count: 0,
    active_email_notification: 0,
    page: PAGE - 1
};

export const emailNotificationSlice = createSlice({
    name: "email_notification",
    initialState,
    reducers: {
        setEmailNotificationPage: (state, action) => {
            state.page = action.payload;
        },
        setEmailNotificationModalOpen: (state, action) => {
            state.is_email_notification_modal_open = action.payload;
        },
        setAction: (state, action) => {
            state.c_action = action.payload;
        },
        setId: (state, action) => {
            state.email_notification_id = action.payload;
        },
        setStatus: (state, action) => {
            state.selected_status = action.payload ? action.payload : '';
        },
        setSearchQuery: (state, action) => {
            state.search_query = action.payload;
        },
        setSelectedData: (state, action) => {
            state.selected_data = action.payload;
        }
    },
    extraReducers: (builder) => {
        // get process list
        builder.addCase(get_email_notification_list.pending, (state) => {
            state.common_loading = true;
        })
        builder.addCase(get_email_notification_list.fulfilled, (state, action) => {
            state.common_loading = false;
            const emailNotificationData = Array.isArray(action.payload.email_notification) ? action.payload.email_notification : [];
            state.email_notification_list = emailNotificationData;
            state.total_email_notification_count = action.payload.total_count || emailNotificationData.length;
            state.active_email_notification = emailNotificationData.filter(item => item.is_active).length;
        })
        builder.addCase(get_email_notification_list.rejected, (state) => {
            state.common_loading = false;
        })
        //create process
        builder.addCase(create_email_notification.fulfilled, (state, action) => {
            state.common_loading = false;
            if (action.payload?.data) {
                state.email_notification_list.push(action.payload.data);
                state.total_email_notification_count = state.email_notification_list.length;
                state.active_email_notification = state.email_notification_list.filter(item => item.is_active).length;
            }
        });
        //update process
        builder.addCase(update_email_notification.fulfilled, (state, action) => {
            state.common_loading = false;
            state.email_notification_list = state.email_notification_list.map((item) =>
                item.id === action.payload.data.id ? { ...item, ...action.payload.data } : item
            );
            state.total_email_notification_count = state.email_notification_list.length;
            state.active_email_notification = state.email_notification_list.filter(item => item.is_active).length;
        });
        // activate_deactivate_process
        builder.addCase(activate_deactivate_email_notification.fulfilled, (state, action) => {
            state.common_loading = false;
            state.email_notification_list = state.email_notification_list.map((item) =>
                item.id === action.payload.data.id ? { ...item, ...action.payload.data } : item
            );
            state.total_email_notification_count = state.email_notification_list.length;
            state.active_email_notification = state.email_notification_list.filter(item => item.is_active).length;
        });
        //get process details
        builder.addCase(get_email_notification_details.pending, (state) => {
            state.common_loading = true;
        })
        builder.addCase(get_email_notification_details.fulfilled, (state, action) => {
            state.common_loading = false;
            state.selected_data = action.payload.data
        })
        builder.addCase(get_email_notification_details.rejected, (state) => {
            state.common_loading = false;
        })
    },
});

// Action creators are generated for each case reducer function
export const {
    setEmailNotificationModalOpen,
    setEmailNotificationPage,
    setAction,
    setId,
    setStatus,
    setSearchQuery,
    setSelectedData
} = emailNotificationSlice.actions;
export default emailNotificationSlice.reducer; 
